import React, { useState, useEffect } from 'react';
import { getAllPaymentCards, addPaymentCard, deletePaymentCard, getDefaultCard, setDefaultCard, validateOtp, authenticatePasskey, registerUser, continueAddCard } from '../utils/apiService';
import CardItem from "./CardItem";
import CreatePasskeyScreen from "./CreatePasskeyScreen";
import AuthenticatePasskeyScreen from "./AuthenticatePasskeyScreen";
import VerifyOtpScreen from "./VerifyOtpScreen";
import chevronUp from "../images/chevron-up.png";

function CardManagement({ sessionId, userId, onCardSelected, showdiscount, showDelete }) {
  
 
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: ''
  });
  const [errors, setErrors] = useState({});
  const [isCardExpanded, setIsCardExpanded] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [cards, setCards] = useState([]);
  const [isShowAddCardPanel, setIsShowAddCardPanel] = useState(false);
  const [defaultCardId, setDefaultCardId] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [showPasskeyScreen, setShowPasskeyScreen] = useState(false);
  const [showCreatePasskeyScreen, setShowCreatePasskeyScreen] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isVerifyingPasskey, setIsVerifyingPasskey] = useState(false);
  const [cardId, setCardId] = useState('');
  /** After create-passkey fails, next OTP success completes the card without passkey */
  const [otpFallbackAfterPasskeyFail, setOtpFallbackAfterPasskeyFail] = useState(false);

  useEffect(() => {
    if (userId) {
      // Fetch all cards
      getAllPaymentCards(userId)
        .then(setCards)
        .catch(() => setCards([]));
      
      // Fetch default card
      getDefaultCard(userId)
        .then(response => {
          if (response.default_card_id) {
            setDefaultCardId(response.default_card_id);
          }
        })
        .catch(err => console.error('Failed to get default card:', err));
    }
  }, [userId]);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      cardNumber: formatted
    }));
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\D/g, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleExpiryDateChange = (e) => {
    const formatted = formatExpiryDate(e.target.value);
    setFormData(prev => ({
      ...prev,
      expiryDate: formatted
    }));
  };

  const handleCvvChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').substring(0, 3);
    setFormData(prev => ({
      ...prev,
      cvv: v
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getCardsByCardNumber = (cardNumber) => {
    return cards.filter(card => card.cardNumber?.slice(-4) === cardNumber?.slice(-4));
  };

  const handleSelectCard = async (cardNumber) => {
    const card = getCardsByCardNumber(cardNumber);

    if (!card || card.length === 0) {
      console.error('Card not found');
      return;
    }

    const selectedCard = card[0];
    
    
    // Update default card on backend
    try {
      await setDefaultCard(userId, selectedCard.card_id);
      setDefaultCardId(selectedCard.card_id);
      console.log('Default card updated:', selectedCard.card_id);
      
      // Notify parent component about card selection
      if (onCardSelected) {
        onCardSelected(selectedCard);
      }
    } catch (error) {
      console.error('Failed to set default card:', error);
    }
  };

  const handleNewCard = async () => {
    const newErrors = {};

    const hasCardData = formData.cardNumber || formData.cardHolder || formData.expiryDate || formData.cvv;
    if (hasCardData) {
      if (!formData.cardNumber.replace(/\s/g, '')) {
        newErrors.cardNumber = 'Card number is required';
      }
      if (!formData.cardHolder.trim()) {
        newErrors.cardHolder = 'Card holder name is required';
      }
      if (!formData.expiryDate) {
        newErrors.expiryDate = 'Expiry date is required';
      }
      if (!formData.cvv) {
        newErrors.cvv = 'CVV is required';
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {

      setShowOtpScreen(false);
      setOtpFallbackAfterPasskeyFail(false);
      const response = await addPaymentCard(
        userId,
        sessionId,
        formData.cardNumber.replace(/\s/g, ''),
        formData.expiryDate,
        formData.cvv,
        formData.cardHolder
      );

      console.log('Add card status:', response);
      setCardId(response.card_id);

      if (response.status === 'SMS_OTP') {
        setShowOtpScreen(true);
        setIsShowAddCardPanel(false);
        //showNotification('Please verify OTP sent to your device', 'success');
      } else {
        // PASSKEY authentication
        setShowPasskeyScreen(true);
        setIsShowAddCardPanel(false);
        //showNotification('Please authenticate with your passkey', 'success');
      }

      // Show OTP screen instead of immediately completing
     
    } catch (error) {
      console.error('Failed to save card details:', error);
      showNotification('Failed to save card', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await deletePaymentCard(userId, cardId);
      setCards((prevCards) => prevCards.filter(card => card.card_id !== cardId));
      showNotification('Card deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete card:', error);
      showNotification('Failed to delete card', 'error');
    }
  };

  const showAddNewCardPanel = () => {
    setIsShowAddCardPanel(true);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const completeCardEnrollmentAfterVerification = async (successMessage) => {
    await continueAddCard(userId, sessionId, cardId);
    const updatedCards = await getAllPaymentCards(userId);
    setCards(updatedCards);
    const defaultCardResponse = await getDefaultCard(userId);
    if (defaultCardResponse.default_card_id) {
      setDefaultCardId(defaultCardResponse.default_card_id);
    }
    setFormData({
      cardNumber: '',
      cardHolder: '',
      expiryDate: '',
      cvv: ''
    });
    setShowOtpScreen(false);
    setShowCreatePasskeyScreen(false);
    showNotification(successMessage, 'success');
  };

  const handleVerifyOtp = async () => {
    if (!otpValue.trim()) {
      setOtpError('Please enter OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      await validateOtp(userId, sessionId, otpValue);

      if (otpFallbackAfterPasskeyFail) {
        setOtpFallbackAfterPasskeyFail(false);
        await completeCardEnrollmentAfterVerification('Card added successfully');
        setIsShowAddCardPanel(false);
        return;
      }

      setShowCreatePasskeyScreen(true);
      setIsShowAddCardPanel(false);
    } catch (error) {
      console.error('Authentication failed failed:', error);
      setOtpError(error.message || 'Authentication failed. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCancelOtp = () => {
    setShowOtpScreen(false);
    setOtpValue('');
    setOtpError('');
    setOtpFallbackAfterPasskeyFail(false);
    setIsShowAddCardPanel(true);
  };

  const handleVerifyPasskey = async () => {
    setIsVerifyingPasskey(true);

    try {
      await authenticatePasskey(userId, sessionId);

      const addCardResponse = await continueAddCard(
        userId,
        sessionId,
        cardId
      );
      if (addCardResponse.status === 'ok') {
         showNotification('Add card successfully', 'success');
        
      }

      // Passkey verified successfully - complete the card addition
      const updatedCards = await getAllPaymentCards(userId);
      setCards(updatedCards);

      const defaultCardResponse = await getDefaultCard(userId);
      if (defaultCardResponse.default_card_id) {
        setDefaultCardId(defaultCardResponse.default_card_id);
      }

      // Reset form and close passkey screen
      setFormData({
        cardNumber: '',
        cardHolder: '',
        expiryDate: '',
        cvv: ''
      });
      setShowPasskeyScreen(false);
      
      showNotification('Card added and verified successfully', 'success');
    } catch (error) {
      console.error('Passkey authentication failed:', error);
      showNotification(error.message || 'Passkey authentication failed. Please try again.', 'error');
    } finally {
      setIsVerifyingPasskey(false);
    }
  };

  const handleCreatePasskey = async () => {
    setIsVerifyingPasskey(true);

    try {
      await registerUser(userId, sessionId, true);
      await completeCardEnrollmentAfterVerification('Passkey created and card added successfully');
    } catch (error) {
      console.error('Passkey creation failed:', error);
      setShowCreatePasskeyScreen(false);
      setOtpValue('');
      setOtpError('');
      setOtpFallbackAfterPasskeyFail(true);
      setShowOtpScreen(true);
    } finally {
      setIsVerifyingPasskey(false);
    }
  };

  const handleCancelPasskey = () => {
    setShowPasskeyScreen(false);
    setIsShowAddCardPanel(true);
    setIsVerifyingOtp(false);
  };

  const handleCancelCreatePasskey = () => {
    setShowCreatePasskeyScreen(false);
    setIsShowAddCardPanel(true);
    setIsVerifyingOtp(false);
  }

  return (
    <div style={{ position: 'relative' }}>
      <div className="card-section">
        <div className="card-section-header" onClick={() => setIsCardExpanded(!isCardExpanded)}>
          <h3>Payment method</h3>
          <span className="expand-icon">
            <img 
              src={chevronUp} 
              alt="toggle" 
              style={{ 
                width: '16px', 
                height: '16px',
                transform: isCardExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.2s ease'
              }} 
            />
          </span>
        </div>

        {isCardExpanded && !isShowAddCardPanel && (
          <div className="card-list">
            {cards.map(cardObj => (
              <CardItem
                key={cardObj.card_id}
                card={cardObj}
                onDelete={handleDeleteCard}
                selected={defaultCardId === cardObj.card_id}
                onSelect={handleSelectCard}
                showdiscount={showdiscount}
                showDelete={showDelete}
              />
            ))}
            <div className='div-center'>
              <button
                type="button"
                className="settings-btn save-btn"
                onClick={showAddNewCardPanel}
                style={{ width: '80%', marginTop: '16px', justifyItems: 'center' }}
              >
                + Add new card
              </button>
            </div>
          </div>
        )}
      </div>

      <VerifyOtpScreen
        isVisible={showOtpScreen}
        otpValue={otpValue}
        otpError={otpError}
        isVerifying={isVerifyingOtp}
        onOtpChange={(e) => {
          setOtpValue(e.target.value);
          setOtpError('');
        }}
        onVerify={handleVerifyOtp}
        onCancel={handleCancelOtp}
      />

      <AuthenticatePasskeyScreen
        isVisible={showPasskeyScreen}
        isVerifying={isVerifyingPasskey}
        onVerify={handleVerifyPasskey}
        onCancel={handleCancelPasskey}
      />

      <CreatePasskeyScreen
        isVisible={showCreatePasskeyScreen}
        isVerifying={isVerifyingPasskey}
        onVerify={handleCreatePasskey}
        onCancel={handleCancelCreatePasskey}
      />

      {isShowAddCardPanel && (
        <div className="card-section">
          <div className="card-section-header" onClick={() => setIsShowAddCardPanel(!isShowAddCardPanel)}>
            <h3>Add new card</h3>
            <span className="expand-icon">
              <img 
                src={chevronUp} 
                alt="toggle" 
                style={{ 
                  width: '16px', 
                  height: '16px',
                  transform: isShowAddCardPanel ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s ease'
                }} 
              />
            </span>
          </div>

          <div className="card-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cardNumber">Card Number</label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleCardNumberChange}
                  placeholder="4532 1234 5678 9012"
                  maxLength="19"
                  className={errors.cardNumber ? 'error' : ''}
                />
                {errors.cardNumber && <span className="error-message">{errors.cardNumber}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cardHolder">Card Holder Name</label>
                <input
                  type="text"
                  id="cardHolder"
                  name="cardHolder"
                  value={formData.cardHolder}
                  onChange={handleChange}
                  placeholder="JOHN DOE"
                  className={errors.cardHolder ? 'error' : ''}
                />
                {errors.cardHolder && <span className="error-message">{errors.cardHolder}</span>}
              </div>
            </div>

            <div className="form-row form-row-split">
              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date</label>
                <input
                  type="text"
                  id="expiryDate"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleExpiryDateChange}
                  placeholder="MM/YY"
                  maxLength="5"
                  className={errors.expiryDate ? 'error' : ''}
                />
                {errors.expiryDate && <span className="error-message">{errors.expiryDate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="cvv">CVV</label>
                <input
                  type="text"
                  id="cvv"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleCvvChange}
                  placeholder="123"
                  maxLength="3"
                  className={errors.cvv ? 'error' : ''}
                />
                {errors.cvv && <span className="error-message">{errors.cvv}</span>}
              </div>
            </div>

            <div className='div-centered'>
              <button
                type="button"
                className="settings-btn save-btn"
                onClick={handleNewCard}
                disabled={isSaving}
                style={{ width: '100%', marginTop: '16px' }}
              >
                {isSaving ? 'Saving...' : 'Add payment card'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup */}
      {notification.show && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '16px 24px',
          backgroundColor: notification.type === 'success' ? '#4CAF50' : '#f44336',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          fontSize: '14px',
          fontWeight: '500',
          minWidth: '250px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          transition: 'opacity 0.3s ease-out',
          opacity: 1
        }}>
          <span style={{ fontSize: '20px' }}>
            {notification.type === 'success' ? '✓' : '✕'}
          </span>
          {notification.message}
        </div>
      )}
    </div>
  );
}

export default CardManagement;
